/*
 *   Copyright (c) 2021 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation  (collectively the "Software") are protected by trade secret, patent,  and copyright laws. The use of the Software is governed by a license  agreement. Disclosure of the Software to third parties, in any form,  in whole or in part, is expressly prohibited except as authorized by the license agreement.
 */
import { getDocs, onSnapshot } from "firebase/firestore";
import { default as mitt } from "mitt";
import { DocumentEntity } from "./document-entity";
import { DefaultFetchDataOptions, DefaultSubsetConstructorOptions } from "./entity-interfaces";
import { SnapshotEventType } from "./snapshot-event";
import { assertId } from "./firebase-assertions";
export function EmptySubset() {
    return new SubsetEntity(null);
}
export class SubsetEntity {
    constructor(ref = null, options = DefaultSubsetConstructorOptions()) {
        this.collection = options.collection;
        this.items = [];
        this.listenUnsubscribe = null;
        this.listenPromiseResolved = false;
        this.emitter = mitt();
        if (ref) {
            this.setReference(ref);
        }
        if (options.listen) {
            this.listen();
        }
    }
    get length() {
        return this.items.length;
    }
    get size() {
        return this.length;
    }
    countWhere(conditionFn) {
        var count = 0;
        count = this.dataItems().reduce((sum, val) => {
            if (conditionFn(val)) {
                return sum + 1;
            }
            else {
                return sum;
            }
        }, count);
        return count;
    }
    updateEach(updateFn) {
        let promises = [];
        for (let i = 0; i < this.items.length; i++) {
            let doc = this.items[i];
            promises.push(updateFn(doc));
        }
        return Promise.all(promises);
    }
    sort(compareFn) {
        return this.items.sort(compareFn);
    }
    setReference(ref) {
        this.resetLocalData();
        if (this.listenUnsubscribe) {
            this.listenUnsubscribe();
        }
        this._ref = ref;
    }
    resetLocalData() {
        while (this.items.length > 0) {
            this.items.pop();
        }
    }
    assertReference() {
        if (!this._ref) {
            throw new Error(`SubsetEntity requires reference for this method`);
        }
    }
    dataItems(options = DefaultFetchDataOptions()) {
        return this.items.map(item => {
            return item.data(options);
        });
    }
    partialItems(fields, options) {
        return this.items.map(item => {
            return item.partial(fields, options);
        });
    }
    ref() {
        return this._ref;
    }
    onUpdate(callback) {
        this.emitter.on(SnapshotEventType.Updated, callback);
    }
    emitUpdate() {
        this.emitter.emit(SnapshotEventType.Updated, {
            type: SnapshotEventType.Updated,
            target: this
        });
    }
    /**
     * Adds a DocumentEntity for the given DocumentSnapshot or re-uses it if it doesn't exist
     * @param snap
     * @returns
     */
    recycle(snap) {
        var match = this.items.find(item => item.path === snap.ref.path);
        if (match) {
            match.applySnapshot(snap);
        }
        else {
            match = this.collection.recycler.updateOrGenerate(snap, snap => {
                return new DocumentEntity(this.collection, snap.ref, snap.data());
            });
            this.items.push(match);
        }
        return match;
    }
    /**
     * Removes any items that aren't in the snapshot
     * @param snap
     */
    removeUnmatched(querySnap) {
        var items = [...this.items];
        while (items.length > 0) {
            var item = items.pop();
            let match = querySnap.docs.find(snap => item.path === snap.ref.path);
            // If something exists locally but not remotely, remove it
            if (!match) {
                var index = this.items.indexOf(item);
                this.items.splice(index, 1);
            }
        }
    }
    listen() {
        this.assertReference();
        if (!this.listenUnsubscribe) {
            this.listenPromise = new Promise((resolve, reject) => {
                this.listenUnsubscribe = onSnapshot(this.ref(), querySnap => {
                    // Apply the new data that just came in
                    this.applyQuerySnapshot(querySnap);
                    // If the listen promise hasn't resolved exactly once yet, resolve it
                    if (!this.listenPromiseResolved) {
                        this.listenPromiseResolved = true;
                        resolve();
                    }
                });
            });
        }
        return this.listenPromise;
    }
    applyQuerySnapshot(querySnap) {
        querySnap.docs.forEach(snap => {
            this.recycle(snap);
        });
        this.removeUnmatched(querySnap);
        this.emitUpdate();
        return this.items;
    }
    itemById(id) {
        assertId(id);
        this.items.sort();
        return this.items.find(item => item.$id === id);
    }
    /**
     * Fetch the latest data for the items in the subset
     * @returns
     */
    async fetch() {
        this.assertReference();
        const querySnap = await getDocs(this.ref());
        return this.applyQuerySnapshot(querySnap);
    }
    cleanup() {
        this.emitter.all.clear();
        if (this.listenUnsubscribe) {
            this.listenUnsubscribe();
            this.listenUnsubscribe = null;
        }
    }
    // Exception tips for developers
    add() {
        throw new Error(`No such function: subset.add. Call subset.collection.add instead`);
    }
    deleteDoc() {
        throw new Error(`No such function: subset.deleteDoc. Call subset.collection.deleteDoc instead`);
    }
    fetchDoc() {
        throw new Error(`No such function: subset.fetchDoc. Call subset.collection.fetchDoc instead`);
    }
    all() {
        throw new Error(`No such function: subset.all. Call subset.collection.all instead`);
    }
    fetchDocWithQuery() {
        throw new Error(`No such function: subset.fetchDocWithQuery. Call subset.collection.fetchDocWithQuery instead`);
    }
    subset() {
        throw new Error(`No such function: subset.subset. Call subset.collection.subset instead`);
    }
    /**
     * Iterates through a specific subcollection by its RELATIONSHIP NAME of each document in the collection
     * Does NOT fetch, just gives you the sub-collection. Order of execution is not guaranteed.
     * @param name
     * @param fn
     * @returns
     */
    async eachSubCollection(name, fn) {
        await this.fetch();
        var promises = [];
        for (let i = 0; i < this.items.length; i++) {
            var item = this.items[i];
            var sub = item.subCollectionEntity(name);
            promises.push(Promise.resolve(fn(item, sub)));
        }
        return await Promise.all(promises);
    }
}
//# sourceMappingURL=subset-entity.js.map